import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Clock } from "lucide-react";
import tracking from "../../lib/tracking";
import { Trans, useT } from "@/lib/i18n";
import Logo from "../../components/Logo";
import { buildPrefillParam } from "../../lib/quizPrefill";

/**
 * Quiz-first landing (Timeleft structure, Outclose look): the first quiz
 * question sits directly on the page and tapping an answer IS the CTA — it
 * deep-links into the onboarding quiz with the answer pre-seeded (see
 * lib/quizPrefill.ts), so the funnel continues from question two.
 *
 * First variant: coffee, gay men 20–39 (audience param `m`).
 */

const EMPHASIS =
  "underline decoration-primary-500 decoration-[3px] underline-offset-[6px]";

// The embedded question — groupChatRole: a fun, identity-flattering opener
// that echoes the "Meet your new groupchat." headline. It isn't asked in
// onboarding (profile-only field), so nothing needs skipping in the stepper;
// the answer still saves to the profile and feeds the vibe reveal. Values
// must stay in sync with the PRESEEDABLE whitelist in quizPrefill.ts and
// the quiz.questions.groupChatRole options in the app namespace.
const QUESTION_ID = "groupChatRole";
const OPTIONS = ["memes", "hype", "thoughtful", "inclusive"] as const;

function quizHref(audienceParam: string, answerValue: string): string {
  const { hostname } = window.location;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";
  const base = isLocal ? "/app/quiz" : "https://app.outclose.com/quiz";
  return `${base}?a=${audienceParam}&${buildPrefillParam(QUESTION_ID, answerValue)}`;
}

interface LandingCoffeeQuizProps {
  posthogEvent: string;
  audienceParam: "m" | "s";
  variant: string;
}

function LandingCoffeeQuiz({
  posthogEvent,
  audienceParam,
  variant,
}: LandingCoffeeQuizProps) {
  const { t } = useT("landing");
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    tracking.trackPage(posthogEvent);
  }, [posthogEvent]);

  const handleAnswer = (value: string) => {
    if (selected) return;
    setSelected(value);
    tracking.track("landing_quiz_answered", {
      landing_variant: variant,
      question_id: QUESTION_ID,
      answer: value,
    });
    // Brief beat so the selection registers visually before the hop.
    setTimeout(() => {
      window.location.href = quizHref(audienceParam, value);
    }, 300);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex flex-col items-center text-center w-full max-w-sm mx-auto px-6 pt-8 pb-6">
        <Logo withIcon={true} className="mb-4" />

        <h1 className="text-3xl md:text-4xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins leading-tight mb-4 w-full">
          <Trans
            ns="landing"
            i18nKey="minimal.variants.men2039.headline"
            components={{ emphasis: <span className={EMPHASIS} /> }}
          />
        </h1>

        <p className="text-sm font-medium text-dark-muted dark:text-neutral-300">
          {t("coffeeQuiz.concept")}
        </p>

        <span className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold bg-primary-500/10 text-primary-600 dark:text-primary-400 mt-3 mb-4">
          <Clock className="w-4 h-4" />
          {t("coffeeQuiz.pill")}
        </span>

        <h2 className="text-xl font-bold text-type-950 dark:text-neutral-50 font-Poppins mb-4">
          {t("coffeeQuiz.question")}
        </h2>

        <div className="flex flex-col gap-3 w-full">
          {OPTIONS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleAnswer(value)}
              className={`w-full rounded-2xl px-5 py-4 text-left text-base font-semibold shadow-sm transition-colors ${
                selected === value
                  ? "bg-primary-500 text-white"
                  : "bg-white dark:bg-neutral-800 text-type-950 dark:text-neutral-50 hover:bg-neutral-100 dark:hover:bg-neutral-700"
              }`}
            >
              {t(`coffeeQuiz.options.${value}`)}
            </button>
          ))}
        </div>

        <p className="text-xs text-dark-muted dark:text-neutral-500 mt-5">
          <Trans
            ns="landing"
            i18nKey="coffeeQuiz.terms"
            components={{
              terms: (
                <Link
                  href="/terms"
                  className="underline hover:text-primary-500 transition-colors"
                />
              ),
              privacy: (
                <Link
                  href="/privacy"
                  className="underline hover:text-primary-500 transition-colors"
                />
              ),
            }}
          />
        </p>
      </div>
    </div>
  );
}

export function CoffeeQuizMen2039() {
  return (
    <LandingCoffeeQuiz
      posthogEvent="coffee_quiz_men_landing"
      audienceParam="m"
      variant="coffee_m2039"
    />
  );
}

export default LandingCoffeeQuiz;
